import React, { memo, useState, useEffect, useContext, useRef } from 'react'
import { graphql, Link as GatsbyLink } from 'gatsby'
import Layout from '../layouts/layout';
import Breadcrumb from '../components/breadcrumb';
import ProductGallery from '../components/product-gallery';
import AddToCartForm from '../components/add-to-cart-form';
import { parseShopifyMetafields, slugify } from '../utils/helpers';
import ProductCarousel from '../partials/common/product-carousel';
import SignUpBanner from '../partials/common/cta-banner';
import Dropdown from 'react-dropdown';
import 'react-dropdown/style.css';
import { StoreContext } from '../context/store-context';
import FrequentlyBoughtWith from '../components/Product/frequently-bought-with';
import CustomSeo from '../components/customSeo';
import ProductContactButton from '../components/product-contact-button';
import Link from '../components/link';

const Product = memo(({ data, location }) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const tabContent = useRef(null);

  const prod = data.shopifyProduct;
  const metafields = parseShopifyMetafields(prod.metafields);
  const [currentVariant, /* setCurrentVariant */] = useState(prod.variants[0])

  const hasBulkDiscount = false;
  const shortDescription = metafields?.product?.shortDescription;
  const hidingAddToCart = metafields?.hiding?.hidingAddToCart;
  const customTextAfterAddToCart = metafields?.hiding?.hidingCutOptions;
  const relatedProducts = data.related.nodes.length > 0 ? data.related.nodes : data.relatedFallback.nodes;
  const alsoViewed =  data.prodsInSameCollections.nodes || [];
  const { addViewedProduct, selectedCategory, setSelectedCategory } = useContext(StoreContext);
  const [searchParams, setSearchParams] = useState('');

  useEffect(() => {
    addViewedProduct(prod)
  }, [prod, addViewedProduct])

  useEffect(() => {
    window.StampedFn?.init()
    window.StampedFn?.reloadUGC()
  }, [])

  useEffect(() => {
    window.dataLayer?.push({
      event: 'product_view',
      pageTitle: prod.title,
      pagePath: location.href,
    })
  }, [location.href, prod.title])

  const tabs = []

  if(prod.descriptionHtml) {
    tabs.push({
      title: 'Description',
      content: prod.descriptionHtml
    })
  }

  if(metafields?.overview_tab?.overview_tab_title && metafields?.overview_tab?.overview_tab_content) {
    for(let i = 0; i < metafields?.overview_tab?.overview_tab_content?.length || 0; i++) {
      if(metafields?.overview_tab?.overview_tab_content[i]) {
        tabs.push({
          title: metafields?.overview_tab?.overview_tab_title[i],
          content: metafields?.overview_tab?.overview_tab_content[i],
        })
      }
    }
  }

  // Hiding broken images in content
  useEffect(() => {
    const images = tabContent?.current?.querySelectorAll('img');
    if (images) {
      Array.from(images).forEach(img => {
        img.onerror = () => {
          img.classList.add('hide');
        }
      })
    }
  }, [activeTabIndex])

  useEffect(() => {
    if (typeof window !== "undefined") {
      setSearchParams(localStorage.getItem('searchParams') || '');
    }
  }, []);

  useEffect(() => {
    return () => {
      setSelectedCategory([]);
    };
  }, [setSelectedCategory]);

  return (
    <Layout>
      <CustomSeo
        title={prod.seo.title || prod.title}
        description={prod.seo.description || prod.title}
        image={prod.featuredImage?.originalSrc}
      />
      <div className="ProductPage">
        <div className="Container">
          <Breadcrumb
            location={location}
            currentTitle={prod.title}
            backPath={location.state?.prevPath
              ? (location.state.prevPath.includes('/search')
                ? location.state?.prevPath + searchParams
                : location.state?.prevPath)
            : false}
            backText={location.state?.prevPath
              ? (location.state.prevPath.includes('/search')
                ? 'Back to Results'
                : 'Back to Shop')
              : false}
            collectionHierarchy={location.state?.collectionHierarchy || []}
            collectionFilter={selectedCategory}
          />
          <div className="ProductPage__main">
            <section className="ProductPage__main__inner">

              <ProductGallery
                allMedia={prod.media}
                variants={prod.variants}
                defaultImage={prod.featuredImage}
                currentVariant={currentVariant}
                productTitle={prod.title}
              />

              <div className={`ProductPage__content ${customTextAfterAddToCart ? 'ProductPage__content--afterAddToCart' : ''}`}>
                <div className="ProductPage__content__top">
                  {prod.vendor && prod.vendor !== 'vendor-unknown' && (
                    <p className="ProductPage__content__vendor">{prod.vendor}</p>
                  )}

                  <h1 className="ProductPage__content__title">{prod.title}</h1>

                  <div className="ProductPage__content__rating">
                    <span
                      className="stamped-product-reviews-badge stamped-main-badge"
                      data-id={prod.legacyResourceId} />
                  </div>
                </div>

                {
                  hidingAddToCart === "true" || hidingAddToCart === true ? (
                    <ProductContactButton product={prod} metafields={metafields} />
                  ) : (
                    <AddToCartForm product={prod} productPage={false} />
                  )
                }
                
                {
                  customTextAfterAddToCart && (
                    <p className='ProductPage__content__afterAddToCart'>
                      <p>
                        <Link to={`/contact`}>Contact us</Link> about cutting your untreated lumber down for transport.
                      </p>
                    </p>
                  )
                }

                {hasBulkDiscount && (
                  <div className="ProductPage__content__bulkbadge">
                    <b>Bulk Quantity Discount:</b> Buy 5 or more get <u>10% off</u>
                  </div>
                )}

                {shortDescription && (
                  <div className="ProductPage__content__description">
                    <h6>About This Product</h6>
                    <div dangerouslySetInnerHTML={{__html: shortDescription }} />
                    <GatsbyLink
                      to="#overview"
                      className="ProductPage__content__description--more"
                    >
                      View More Information
                    </GatsbyLink>
                  </div>
                )}

              </div>
            </section>

            {data.fbwProducts.nodes.length > 1 && (
              <FrequentlyBoughtWith products={data.fbwProducts.nodes} parentProd={prod} />
            )}

            {tabs.length > 0 && (
              <section className="ProductPage__overview" id="overview">
                <div className="ProductPage__overview__inner">
                  <h2 className='ProductPage__overview__title'>Product Overview</h2>

                  <div className="ProductPage__overview__tabs">
                    {tabs.map((tab, i) => (
                      <button
                        key={i}
                        className={`notbutton ProductPage__overview__tab${activeTabIndex === i ? ' active' : ''}`}
                        onClick={() => setActiveTabIndex(i)}
                      >{tab.title}</button>
                    ))}

                    <Dropdown
                      options={tabs.map(t => t.title)}
                      onChange={e => setActiveTabIndex(tabs.map(t => t.title).indexOf(e.value))}
                      value={tabs[0].title}
                      placeholder="Select an option"
                    />
                  </div>

                  <div
                    ref={tabContent}
                    className={`ProductPage__overview__content ProductPage__overview__content--${slugify(tabs[activeTabIndex].title)}`}
                    dangerouslySetInnerHTML={{__html: tabs[activeTabIndex].content}}
                  />
                </div>

              </section>
            )}

            <section className="ProductPage__reviews">
              <div className="ProductPage__reviews__inner">
                <div className="ProductPage__reviews__title__wrap">
                  <h2 className="ProductPage__reviews__title">Customer Reviews</h2>
                </div>
                <div
                  id="stamped-main-widget"
                  data-product-id={prod.legacyResourceId}
                  data-name={prod.title}
                  data-url={location.origin + location.pathname}
                  data-image-url={prod.featuredImage?.originalSrc}
                  data-description={prod.description}
                  data-product-sku={currentVariant?.sku}
                ></div>
              </div>
            </section>

            {relatedProducts.length > 1 && (
              <ProductCarousel
                products={relatedProducts}
                title="Related Products"
                className="ProductPage__related__carousel"
              />
            )}

          </div>
        </div>

        <SignUpBanner />

        {alsoViewed.length > 0 && (
          <section className="ProductPage__alsoviewed">
            <div className="Container ProductPage__alsoviewed__inner">
              <ProductCarousel
                products={alsoViewed}
                title="Customers Also Viewed"
                className="ProductPage__alsoviewed__carousel"
              />
            </div>
          </section>
        )}
      </div>
      {metafields?.column?.columnCount && (
          <style>
            {`
              .ProductPage__overview__content--description ul {
                columns: ${metafields?.column?.columnCount};
              }
            `}
          </style>
        )
      }
    </Layout>
  )
})

export default Product

export const productQuery = graphql`
  query currentProductQuery($id: String, $related: [String], $collections: [String], $fbwProducts: [String]) {
    shopifyProduct(id: {eq: $id }) {
      legacyResourceId
      storefrontId
      title
      handle
      vendor
      descriptionHtml
      featuredImage {
        originalSrc
      }
      priceRangeV2 {
        maxVariantPrice {
          amount
        }
        minVariantPrice {
          amount
        }
      }
      options {
        name
        values
      }
      metafields {
        key
        namespace
        value
      }
      media {
        ... on ShopifyMediaImage {
          mediaContentType
          image {
            src
            altText
            originalSrc
          }
        }
        ... on ShopifyVideo {
          mediaContentType
          preview {
            image {
              src
              altText
              originalSrc
            }
          }
          sources {
            url
          }
        }
      }
      variants {
        title
        price
        compareAtPrice
        legacyResourceId
        storefrontId
        availableForSale
        sku
        selectedOptions {
          name
          value
        }
        media {
          ... on ShopifyMediaImage {
            image {
              altText
              src
              originalSrc
            }
          }
        }
      }
      seo {
        title
        description
      }
    }
    fbwProducts: allShopifyProduct(filter: {
      legacyResourceId: {in: $fbwProducts},
      priceRangeV2: {
        maxVariantPrice: {amount: {gt: 0.01}},
        minVariantPrice: {amount: {gt: 0.01}}
      },
      status: {eq: ACTIVE}
    }) {
      nodes {
        title
        handle
        featuredImage {
          altText
          src
        }
        variants {
          storefrontId
          price
          compareAtPrice
          availableForSale
          legacyResourceId
          selectedOptions {
            name
          }
        }
      }
    }
    related: allShopifyProduct(
      filter: {
        handle: {in: $related},
        priceRangeV2: {
          maxVariantPrice: {amount: {gt: 0.01}},
          minVariantPrice: {amount: {gt: 0.01}}
        },
        status: {eq: ACTIVE}
      }
    ) {
      nodes {
        title
        handle
        vendor
        shopifyId
        badge: metafield(namespace: "badge", key: "badge") {
          value
        }
        featuredImage {
          altText
          src
        }
        priceRangeV2 {
          maxVariantPrice {
            amount
          }
          minVariantPrice {
            amount
          }
        }
        variants {
          storefrontId
          price
          compareAtPrice
          availableForSale
          selectedOptions {
            name
          }
        }
      }
    }
    relatedFallback: allShopifyProduct(
      filter: {
        collections: {elemMatch: {handle: {in: $collections}}},
        priceRangeV2: {
          maxVariantPrice: {amount: {gt: 0.01}},
          minVariantPrice: {amount: {gt: 0.01}}
        },
        status: {eq: ACTIVE}
      }
      limit: 8
    ) {
      nodes {
        title
        handle
        vendor
        shopifyId
        badge: metafield(namespace: "badge", key: "badge") {
          value
        }
        featuredImage {
          altText
          src
        }
        priceRangeV2 {
          maxVariantPrice {
            amount
          }
          minVariantPrice {
            amount
          }
        }
        variants {
          storefrontId
          price
          compareAtPrice
          availableForSale
          selectedOptions {
            name
          }
        }
      }
    }
    prodsInSameCollections: allShopifyProduct(
      filter: {collections: {elemMatch: {handle: {in: $collections}}}, id: {ne: $id}},
      limit: 8
    ) {
      nodes {
        title
        handle
        vendor
        shopifyId
        badge: metafield(namespace: "badge", key: "badge") {
          value
        }
        featuredImage {
          altText
          src
        }
        priceRangeV2 {
          maxVariantPrice {
            amount
          }
          minVariantPrice {
            amount
          }
        }
        variants {
          storefrontId
          price
          compareAtPrice
          availableForSale
          selectedOptions {
            name
          }
        }
      }
    }
  }
`;